import React, { useState, useEffect } from 'react';
import { ConfigProvider } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useTheme } from '../../../theme/ThemeContext';
import Tooltip from '../../../atoms/Tooltip/Tooltip';
import Chevron from '../../../atoms/Icon/Chevron';
import ArrowRight from '../../../atoms/Icon/ArrowRight';
import Target from '../../../atoms/Icon/Target';
import { agenticMarkdownComponents, fixUnclosedColorSpans } from '../../../shared/markdownComponents';
import { AgenticPhaseCard } from '../AskAsperAgentic.types';

export interface PhaseCardProps {
    phase: AgenticPhaseCard;
    onViewArtefact?: (artefactId: string, subtaskId: string, phaseId: string) => void;
    /** When true, start collapsed (e.g. when summary text has arrived) */
    defaultCollapsed?: boolean;
    /** Callback when the user toggles the "Show Task" / "Hide Task" button (receives next collapsed state). */
    onToggleCollapsed?: (collapsed: boolean, phase: AgenticPhaseCard) => void;
    'data-test'?: string;
    'data-test-id'?: string;
}

/**
 * PhaseCard - Phase card component matching Figma design
 *
 * Displays:
 * - Phase title
 * - Current task badge (pill-shaped)
 * - Analysis subtasks with crosshair icons
 * - View Artefact buttons (purple button style)
 */
const PhaseCard: React.FC<PhaseCardProps> = ({
    phase,
    onViewArtefact,
    defaultCollapsed = false,
    onToggleCollapsed,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();
    const [collapsed, setCollapsed] = useState(defaultCollapsed);

    // Sync with defaultCollapsed when summary arrives (e.g. auto-collapse when pair gets summary)
    useEffect(() => {
        setCollapsed(defaultCollapsed);
    }, [defaultCollapsed]);

    const handleViewArtefact = (artefactId: string, subtaskId: string) => {
        if (onViewArtefact) {
            onViewArtefact(artefactId, subtaskId, phase.id);
        }
    };

    // Simplified format: title and single overview line only (no subtasks)
    const isSimplifiedFormat = phase.overview != null && (!phase.subtasks || phase.subtasks.length === 0);
    const hasTaskContent = (phase.subtasks && phase.subtasks.length > 0) || (isSimplifiedFormat && phase.overview);
    const showToggle = !!hasTaskContent;

    const handleToggleTask = () => {
        setCollapsed((prev) => {
            const next = !prev;
            onToggleCollapsed?.(next, phase);
            return next;
        });
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-white rounded-lg border border-[rgba(0,0,0,0.1)] p-4"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-phase-card
                data-phase-status={phase.status}
            >
                {/* Phase Header - Title + Show/Hide Task toggle */}
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        {phase.phaseNumber && !isSimplifiedFormat && (
                            <span className="font-['Open_Sans'] font-semibold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)]">
                                Task {phase.phaseNumber}:
                            </span>
                        )}
                        <span className="font-['Open_Sans'] font-semibold text-[16px] leading-[24px] text-black">
                            {phase.title}
                        </span>
                    </div>
                    {showToggle && (
                        <button
                            type="button"
                            onClick={handleToggleTask}
                            className="flex items-center gap-1 font-['Open_Sans'] text-[14px] leading-[20px] text-[#5F49BC] hover:text-[#4c3d94] transition-colors"
                            data-test-id={`${dataTestId}-toggle-task`}
                        >
                            {collapsed ? 'Show Task' : 'Hide Task'}
                            <Chevron
                                size={14}
                                rotate={collapsed ? 180 : 0}
                                color="currentColor"
                                className="shrink-0"
                            />
                        </button>
                    )}
                </div>

                {/* Middle strip (gradient) - "Executing tasks ..." or phase.description */}
                <div className="mb-3">
                    <span
                        className="block w-full px-4 py-2 rounded-[12px] font-['Open_Sans'] font-normal text-[16px] leading-[24px] text-[#363636] text-left"
                        style={{
                            background: 'linear-gradient(to right, #FCEEFC, #FEFAF0)',
                        }}
                    >
                        {phase.description ?? 'Executing tasks ...'}
                    </span>
                </div>

                {/* Single overview line (simplified format) - bold one-liner only */}
                {!collapsed && isSimplifiedFormat && phase.overview && (
                    <div className="flex gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <Target size={16} color="#7C3AED" />
                        </div>
                        <div className="flex-1 min-w-0 font-['Open_Sans'] font-semibold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] markdown-content">
                            <ReactMarkdown
                                rehypePlugins={[rehypeRaw]}
                                remarkPlugins={[remarkGfm]}
                                remarkRehypeOptions={{ allowDangerousHtml: true }}
                                components={agenticMarkdownComponents}
                            >
                                {fixUnclosedColorSpans(phase.overview)}
                            </ReactMarkdown>
                        </div>
                    </div>
                )}

                {/* View Artefact (when taskId available - links to visualization in right panel) */}
                {phase.taskId && onViewArtefact && (
                    <Tooltip title="Jump to Artefact" placement="top" getPopupContainer={() => document.body}>
                        <button
                            type="button"
                            onClick={() => handleViewArtefact(phase.taskId!, phase.id)}
                            className="mt-3 flex items-center gap-1 bg-[#F7F5FF] hover:bg-[#E9D5FF] text-[#5F49BC] font-['Open_Sans'] text-[16px] leading-[24px] px-3 py-1 rounded transition-colors"
                            data-test-id={`${dataTestId}-view-artefact`}
                            aria-label="Jump to Artefact"
                        >
                            View Artefact
                            <Chevron size={14} rotate={90} color="currentColor" />
                        </button>
                    </Tooltip>
                )}

                {/* Legacy: Subtasks */}
                {!collapsed && !isSimplifiedFormat && phase.subtasks && phase.subtasks.length > 0 && (
                    <div className="space-y-4">
                        {phase.subtasks.map((subtask, index) => (
                            <div
                                key={subtask.id || index}
                                className="flex gap-3"
                                data-test-id={`${dataTestId}-subtask-${index}`}
                            >
                                <div className="flex-shrink-0 mt-0.5">
                                    <Target size={16} color="#7C3AED" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h5 className="font-['Open_Sans'] font-semibold text-[16px] leading-[24px] text-black mb-1">
                                        {subtask.title}
                                    </h5>
                                    {subtask.artefactId && (
                                        <button
                                            onClick={() => handleViewArtefact(subtask.artefactId!, subtask.id)}
                                            className="flex items-center gap-1 bg-[#F3E8FF] hover:bg-[#E9D5FF] text-[#7C3AED] font-['Open_Sans'] text-[16px] leading-[24px] px-3 py-1.5 rounded transition-colors"
                                            data-test-id={`${dataTestId}-subtask-${index}-view-artefact`}
                                        >
                                            View Artefact
                                            <ArrowRight size={14} color="currentColor" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default PhaseCard;
